import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Botao } from "../ui";
import {
  CHAVE_DO_WEBCHAT,
  acrescentarMensagem,
  horaDaMensagem,
  mudaOEstado,
  rotuloDoAutor,
  sinalizarDigitando,
  useConversaAoVivo,
  useEnviarMensagem,
  useMensagensDaConversa,
  type LadoDoChat,
  type MensagemDoChat,
} from "../../nucleo/webchat";
import { mensagemDoConsole } from "../../nucleo/console";

/** Quanto tempo o "digitando…" fica na tela depois do último sinal. */
export const DURACAO_DO_DIGITANDO_MS = 6000;
/** De quanto em quanto tempo o sinal de digitação sai enquanto a pessoa digita. */
export const INTERVALO_DO_SINAL_MS = 3000;

export interface PropsDaConversa {
  lado: LadoDoChat;
  conversaId: string;
  /** Quando a conversa aceita novas falas. Encerrada, o campo de texto some. */
  podeEscrever: boolean;
  /** A empresa deixa o "digitando…" ligado? Desligado, nenhum sinal sai daqui. */
  digitandoAtivo: boolean;
  /** Rótulo acessível do campo de texto. */
  rotuloDoCampo?: string;
}

/**
 * O miolo de uma conversa: as falas, o "digitando…" do outro lado e o campo de resposta.
 * É o mesmo componente nos dois lados — o que muda é o `lado`, que escolhe o caminho da API e o
 * rótulo de cada autor. As falas chegam pelo fluxo ao vivo e pela resposta do próprio envio; o
 * cache as junta sem repetir.
 */
export function Conversa({
  lado,
  conversaId,
  podeEscrever,
  digitandoAtivo,
  rotuloDoCampo = "Escreva a sua mensagem",
}: PropsDaConversa) {
  const cliente = useQueryClient();
  const mensagens = useMensagensDaConversa(lado, conversaId);
  const enviar = useEnviarMensagem(lado, conversaId);
  const [texto, setTexto] = useState("");
  const [conectado, setConectado] = useState(true);
  const [outroDigitando, setOutroDigitando] = useState(false);
  const fimDaLista = useRef<HTMLDivElement | null>(null);
  const ultimoSinal = useRef(0);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  useConversaAoVivo(
    lado,
    conversaId,
    true,
    (quadro) => {
      if (quadro.tipo === "digitando") {
        const outro = lado === "cliente" ? "agente" : "cliente";
        if (quadro.quem !== outro) return;
        setOutroDigitando(true);
        if (temporizador.current) clearTimeout(temporizador.current);
        temporizador.current = setTimeout(() => setOutroDigitando(false), DURACAO_DO_DIGITANDO_MS);
        return;
      }
      acrescentarMensagem(cliente, lado, conversaId, quadro.mensagem);
      if (quadro.mensagem.autor_tipo !== "sistema") setOutroDigitando(false);
      if (mudaOEstado(quadro.mensagem)) {
        void cliente.invalidateQueries({ queryKey: CHAVE_DO_WEBCHAT });
      }
    },
    setConectado,
  );

  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );
  const total = mensagens.data?.length ?? 0;
  useEffect(() => {
    fimDaLista.current?.scrollIntoView?.({ block: "end" });
  }, [total, outroDigitando]);

  function aoDigitar(valor: string) {
    setTexto(valor);
    if (!digitandoAtivo || !valor.trim()) return;
    const agora = Date.now();
    if (agora - ultimoSinal.current < INTERVALO_DO_SINAL_MS) return;
    ultimoSinal.current = agora;
    void sinalizarDigitando(lado, conversaId);
  }

  function enviarTexto() {
    const limpo = texto.trim();
    if (!limpo || enviar.isPending) return;
    enviar.mutate(limpo, {
      onSuccess: () => {
        setTexto("");
        ultimoSinal.current = 0;
      },
    });
  }

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    enviarTexto();
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>) {
    if (evento.key === "Enter" && !evento.shiftKey) {
      evento.preventDefault();
      enviarTexto();
    }
  }

  return (
    <div className="chat-conversa" data-testid="chat-conversa">
      <div
        className="chat-conversa__lista"
        role="log"
        aria-live="polite"
        aria-label="Mensagens da conversa"
      >
        {mensagens.isPending ? <p className="chat-conversa__aviso">Carregando a conversa…</p> : null}
        {mensagens.isError ? (
          <p className="chat-conversa__aviso" role="alert">
            {mensagemDoConsole(mensagens.error)}
          </p>
        ) : null}
        {mensagens.data?.map((mensagem) => (
          <Fala key={mensagem.id} mensagem={mensagem} lado={lado} />
        ))}
        {outroDigitando ? (
          <p className="chat-conversa__digitando" data-testid="chat-digitando">
            {lado === "cliente" ? "A equipe está digitando…" : "O cliente está digitando…"}
          </p>
        ) : null}
        <div ref={fimDaLista} />
      </div>
      {!conectado ? (
        <p className="chat-conversa__aviso" role="status" data-testid="chat-reconectando">
          Reconectando… as mensagens voltam a chegar assim que a conexão voltar.
        </p>
      ) : null}
      {podeEscrever ? (
        <form className="chat-conversa__form" onSubmit={aoEnviar}>
          <textarea
            className="chat-conversa__campo"
            aria-label={rotuloDoCampo}
            rows={2}
            maxLength={4000}
            value={texto}
            placeholder="Escreva aqui e tecle Enter para enviar"
            onChange={(evento) => aoDigitar(evento.target.value)}
            onKeyDown={aoTeclar}
          />
          <Botao type="submit" tamanho="pequeno" disabled={!texto.trim() || enviar.isPending}>
            Enviar
          </Botao>
        </form>
      ) : null}
      {enviar.isError ? (
        <p className="chat-conversa__aviso" role="alert">
          {mensagemDoConsole(enviar.error)}
        </p>
      ) : null}
    </div>
  );
}

function Fala({ mensagem, lado }: { mensagem: MensagemDoChat; lado: LadoDoChat }) {
  if (mensagem.autor_tipo === "sistema") {
    return (
      <p className="chat-fala chat-fala--sistema" data-testid="chat-fala-sistema">
        {mensagem.texto}
      </p>
    );
  }
  const minha =
    (lado === "cliente" && mensagem.autor_tipo === "cliente") ||
    (lado === "atendimento" && mensagem.autor_tipo === "agente");
  return (
    <div
      className={minha ? "chat-fala chat-fala--minha" : "chat-fala chat-fala--outra"}
      data-testid="chat-fala"
      data-autor={mensagem.autor_tipo}
    >
      <span className="chat-fala__autor">
        {rotuloDoAutor(mensagem, lado)} · {horaDaMensagem(mensagem.criada_em)}
      </span>
      <span className="chat-fala__texto">{mensagem.texto}</span>
    </div>
  );
}
